// SCREENS-BROWSER-NAV: the address bar of a browser screen, for whoever drives it — back, reload,
// and an address to go to (a bare host becomes https://host; words search DuckDuckGo). It calls the
// broker (`screens.browse`), which drives Camofox on the stack; nothing goes to the browser from
// here directly.
import { Button, Input } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, RotateCwIcon } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";

/** What the person typed, as an address to open: a URL, a host, or a search. */
export function addressOf(typed: string): string | null {
  const t = typed.trim();
  if (!t) return null;
  if (/^https?:\/\/\S+$/i.test(t)) return t;
  if (!/\s/.test(t) && /^[^/\s]+\.[a-z]{2,}(?::\d+)?(\/.*)?$/i.test(t)) return `https://${t}`;
  return `https://duckduckgo.com/?q=${encodeURIComponent(t)}`;
}

type Action =
  | { action: "back" }
  | { action: "reload" }
  | {
      action: "navigate";
      url: string;
    };

/** How often the bar asks where the page is (links clicked inside the page move it). */
export const LOCATION_POLL_MS = 2_500;

export function AddressBar({
  api,
  co,
  leaseId,
  pollMs = LOCATION_POLL_MS,
}: {
  api: Api;
  co: string;
  leaseId: string;
  pollMs?: number;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const editing = useRef(false);
  const key = ["company", co, "screens", "location", leaseId] as const;
  // Where the page is now, while I drive it (the bar is shown only then).
  const where = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      api.client.screens.browse({
        params: { co, id: leaseId },
        body: { action: "location" },
        signal,
      }),
    refetchInterval: pollMs,
    retry: false,
  });
  const url = where.data?.url;
  useEffect(() => {
    if (url !== undefined && !editing.current) setText(url ?? "");
  }, [url]);
  const go = useMutation({
    mutationFn: (body: Action) => api.client.screens.browse({ params: { co, id: leaseId }, body }),
    onSuccess: (r) => {
      editing.current = false;
      qc.setQueryData(key, r);
      setText(r.url ?? "");
      // A redirect lands a moment later: ask again.
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const url = addressOf(text);
    if (!url) return;
    editing.current = false;
    go.mutate({ action: "navigate", url });
  };
  return (
    <form
      data-slot="address-bar"
      onSubmit={submit}
      className="flex h-10 shrink-0 items-center gap-1 border-b border-border px-2"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Back"
        title="Back"
        disabled={go.isPending}
        onClick={() => {
          editing.current = false;
          go.mutate({ action: "back" });
        }}
      >
        <ArrowLeftIcon aria-hidden={true} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Reload"
        title="Reload"
        disabled={go.isPending}
        onClick={() => {
          editing.current = false;
          go.mutate({ action: "reload" });
        }}
      >
        <RotateCwIcon aria-hidden={true} />
      </Button>
      <Input
        aria-label="Address"
        className="h-8 min-w-0 flex-1"
        value={text}
        placeholder="Search or enter address"
        spellCheck={false}
        autoComplete="off"
        onFocus={(e) => e.currentTarget.select()}
        onBlur={() => {
          // Left without opening it: show where the page is again.
          if (!editing.current) return;
          editing.current = false;
          if (url !== undefined) setText(url ?? "");
        }}
        onChange={(e) => {
          editing.current = true;
          setText(e.target.value);
        }}
      />
      {go.isError ? (
        <span role="alert" className="shrink-0 text-xs text-danger-text">
          Couldn't open it
        </span>
      ) : null}
    </form>
  );
}
